import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  MemberTagInput,
  MemberTagPreview,
  MemberTagResult,
  MemberTagDefinitionDto,
  MemberTagDeltaInput,
  MemberTagDeltaPreview,
  MemberTagDeltaResult,
  MemberTagEditor,
  MemberTagScope,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { useDialogFocus } from '../../hooks/useDialogFocus';

const control = 'min-h-11 rounded border border-base-300 bg-base-100 px-3 py-2';
const codeOf = (error: unknown) => {
  const data = (error as { response?: { data?: { errorCode?: string; code?: string } } }).response
    ?.data;
  return data?.errorCode ?? data?.code;
};

export function MemberTagSummary({ memberId }: { memberId: string }) {
  const session = useAuthStore((s) => s.sessionRevision);
  const venue = useAuthStore((s) => sessionVenueKey(s.accessToken));
  return (
    <Summary
      key={`${session}:${venue}:${memberId}`}
      memberId={memberId}
      context={`${session}:${venue}:${memberId}`}
    />
  );
}
function Summary({ memberId, context }: { memberId: string; context: string }) {
  const { t } = useTranslation('member');
  const actions = useAuthStore((s) => s.allowedActions);
  const [open, setOpen] = useState(false);
  const canRead = actions?.includes('member:read') ?? false;
  const canWrite = actions?.some((a) =>
    ['member:tags_assign', 'member:tags_assign_global'].includes(a),
  );
  const summary = useQuery({
    queryKey: ['member-tags-summary', context],
    enabled: canRead,
    retry: false,
    queryFn: () =>
      api.get<{
        data: {
          id: string;
          definition: MemberTagDefinitionDto;
          scope: MemberTagScope;
          source: string;
          automatic?: unknown[];
        }[];
        total: number;
      }>(`/members/${memberId}/tags`),
  });
  if (!canRead) return null;
  return (
    <section
      className="mb-4 rounded-lg border border-base-300 bg-base-100 p-3"
      aria-label={t('tags.field')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-semibold">{t('tags.field')}</h2>
        {summary.isPending ? (
          <span>{t('tags.loading')}</span>
        ) : summary.isError ? (
          <span role="alert">{t('tags.loadFailed')}</span>
        ) : summary.data?.total === 0 ? (
          <span>{t('tags.noTags')}</span>
        ) : (
          summary.data?.data.slice(0, 4).map((a) => (
            <span key={a.id} className="max-w-full break-words rounded bg-base-200 px-2 py-1">
              {a.definition.name} · {t(`tags.${a.scope}`)} · {t(`tags.${a.source}`)}
              {a.source !== 'AUTOMATIC' && !!a.automatic?.length && ` + ${t('tags.AUTOMATIC')}`}
            </span>
          ))
        )}
        <button className={control} onClick={() => setOpen(true)}>
          {t(canWrite ? 'tagEditor.manage' : 'tagEditor.view')}
        </button>
        {(summary.data?.total ?? 0) > 4 && (
          <span>{t('tagEditor.total', { count: summary.data!.total })}</span>
        )}
      </div>
      {open && <Editor memberId={memberId} context={context} onClose={() => setOpen(false)} />}
    </section>
  );
}
function Editor({
  memberId,
  context,
  onClose,
}: {
  memberId: string;
  context: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('member');
  const actions = useAuthStore((s) => s.allowedActions);
  const [scope, setScope] = useState<MemberTagScope>(
    actions?.includes('member:tags_assign') ? 'VENUE' : 'ASSOCIATION',
  );
  const [search, setSearch] = useState(''),
    [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, string> | null>(null);
  const [baseline, setBaseline] = useState<MemberTagEditor | null>(null);
  const [single, setSingle] = useState<{
    assignment: MemberTagEditor['nonAutomatic'][number];
    action: 'REMOVE' | 'SET_EXPIRY';
  } | null>(null);
  const [singleFrozen, setSingleFrozen] = useState<MemberTagInput | null>(null);
  const [singlePreview, setSinglePreview] = useState<MemberTagPreview | null>(null);
  const [expiresOn, setExpiresOn] = useState('');
  const [preview, setPreview] = useState<MemberTagDeltaPreview | null>(null);
  const [frozen, setFrozen] = useState<MemberTagDeltaInput | null>(null);
  const [busy, setBusy] = useState(false),
    [unknown, setUnknown] = useState(false);
  const [error, setError] = useState(''),
    [success, setSuccess] = useState(false);
  const [discard, setDiscard] = useState<MemberTagScope | 'CLOSE' | null>(null);
  const mounted = useRef(true),
    lock = useRef(false),
    epoch = useRef(0);
  useEffect(() => {
    mounted.current = true;
    const generation = epoch;
    return () => {
      mounted.current = false;
      generation.current++;
    };
  }, []);
  const qc = useQueryClient();
  const editor = useQuery({
    queryKey: ['member-tag-editor', context, scope],
    retry: false,
    refetchOnMount: 'always',
    queryFn: () => api.get<MemberTagEditor>(`/members/${memberId}/tags/editor?scope=${scope}`),
  });
  const catalogue = useQuery({
    queryKey: ['member-tag-catalogue', context, search, page],
    retry: false,
    queryFn: () =>
      api.get<{ data: MemberTagDefinitionDto[]; total: number }>(
        `/member-tags?page=${page}&search=${encodeURIComponent(search)}`,
      ),
  });
  useEffect(() => {
    if (editor.data?.scope === scope && !editor.isFetching && !editor.isError && !baseline) {
      setBaseline(editor.data);
      setSelected(
        new Map(
          editor.data.nonAutomatic.filter((a) => a.active).map((a) => [a.tagId, a.definition.name]),
        ),
      );
    }
  }, [editor.data, editor.isFetching, editor.isError, baseline, scope]);
  const initial = baseline?.nonAutomatic.filter((a) => a.active) ?? [];
  const add = [...(selected?.keys() ?? [])]
    .filter((id) => !initial.some((a) => a.tagId === id))
    .map((tagId) => ({ tagId }));
  const remove = initial
    .filter((a) => !selected?.has(a.tagId))
    .map((a) => ({ tagId: a.tagId, assignmentId: a.assignmentId, revision: a.revision }));
  const dirty = add.length + remove.length > 0 || !!single;
  const writable =
    baseline?.scopes.find((s) => s.scope === scope)?.canOperate &&
    actions?.includes(scope === 'VENUE' ? 'member:tags_assign' : 'member:tags_assign_global');
  const blocked = busy || unknown;
  const requestClose = () => {
    if (blocked) return;
    if (dirty && !success) setDiscard('CLOSE');
    else onClose();
  };
  const dialog = useDialogFocus(true, requestClose, blocked);
  const reset = (next: MemberTagScope) => {
    epoch.current++;
    setScope(next);
    setSingle(null);
    setSingleFrozen(null);
    setSinglePreview(null);
    setBaseline(null);
    setSelected(null);
    setPreview(null);
    setFrozen(null);
    setExpiresOn('');
    setSearch('');
    setPage(1);
    setError('');
    setSuccess(false);
    setDiscard(null);
  };
  const toggle = (id: string, name: string) => {
    if (blocked) return;
    setSelected((value) => {
      const next = new Map(value);
      if (next.has(id)) next.delete(id);
      else next.set(id, name);
      return next;
    });
    setPreview(null);
    setFrozen(null);
    setSuccess(false);
    setError('');
  };
  const message = (error: unknown) =>
    t(`tags.errors.${codeOf(error) ?? 'FAILED'}`, { defaultValue: t('tags.errors.FAILED') });
  const refreshDraft = async () => {
    const revision = epoch.current;
    const intendedAdd = add.map(({ tagId }) => [tagId, selected?.get(tagId) ?? tagId] as const);
    const intendedRemove = new Set(remove.map(({ tagId }) => tagId));
    const refreshed = await editor.refetch();
    if (!mounted.current || revision !== epoch.current || refreshed.error || !refreshed.data)
      return;
    const fresh = refreshed.data;
    const next = new Map(
      fresh.nonAutomatic
        .filter((a) => a.active && !intendedRemove.has(a.tagId))
        .map((a) => [a.tagId, a.definition.name]),
    );
    for (const [id, name] of intendedAdd) next.set(id, name);
    setBaseline(fresh);
    setSelected(next);
    if (single) {
      const assignment = fresh.nonAutomatic.find(
        (a) => a.assignmentId === single.assignment.assignmentId && a.open,
      );
      setSingle(assignment ? { ...single, assignment } : null);
    }
  };
  const dry = async () => {
    if (lock.current || !writable || !dirty) return;
    lock.current = true;
    setBusy(true);
    setError('');
    const revision = epoch.current;
    const body: MemberTagDeltaInput = {
      kind: 'MEMBER_TAG_DELTA',
      version: 1,
      operationKey: crypto.randomUUID(),
      scope,
      expiresOn: add.length ? expiresOn || null : null,
      add,
      remove,
    };
    try {
      const value = await api.post<MemberTagDeltaPreview>(
        `/members/${memberId}/tags/delta/preview`,
        body,
      );
      if (mounted.current && revision === epoch.current) {
        if (value.errors.some((e) => e.code === 'TAG_PLAN_STALE')) {
          await refreshDraft();
          setError(t('tags.errors.TAG_PLAN_STALE'));
        } else {
          setPreview(value);
          setFrozen(body);
        }
      }
    } catch (e) {
      if (mounted.current && revision === epoch.current) setError(message(e));
    } finally {
      lock.current = false;
      if (mounted.current && revision === epoch.current) setBusy(false);
    }
  };
  const drySingle = async () => {
    if (lock.current || !writable || !single || !baseline) return;
    lock.current = true;
    setBusy(true);
    setError('');
    const revision = epoch.current;
    const body: MemberTagInput = {
      operationKey: crypto.randomUUID(),
      scope,
      action: single.action,
      tagId: single.assignment.tagId,
      identifierMode: 'MEMBER_NUMBER',
      identifiers: [baseline.member.memberNo],
      expiresOn: single.action === 'SET_EXPIRY' ? expiresOn || null : null,
    };
    try {
      const result = await api.post<MemberTagPreview>('/member-tags/operations/preview', body);
      if (mounted.current && revision === epoch.current) {
        setSingleFrozen(body);
        setSinglePreview(result);
      }
    } catch (e) {
      if (mounted.current && revision === epoch.current) setError(message(e));
    } finally {
      lock.current = false;
      if (mounted.current && revision === epoch.current) setBusy(false);
    }
  };
  const confirm = async () => {
    if (lock.current || (!(frozen && preview?.token) && !(singleFrozen && singlePreview?.token)))
      return;
    lock.current = true;
    setBusy(true);
    setError('');
    const revision = epoch.current,
      wasUnknown = unknown;
    try {
      if (singleFrozen && singlePreview?.token) {
        await api.post<MemberTagResult>('/member-tags/operations/confirm', {
          ...singleFrozen,
          token: singlePreview.token,
        });
      } else {
        const result = await api.post<MemberTagDeltaResult>(
          `/members/${memberId}/tags/delta/confirm`,
          { ...frozen!, token: preview!.token },
        );
        if (result.operationKey !== frozen!.operationKey || result.targetMemberId !== memberId)
          throw new Error('Receipt mismatch');
      }
      if (!mounted.current || revision !== epoch.current) return;
      setSingle(null);
      setSingleFrozen(null);
      setSinglePreview(null);
      setUnknown(false);
      setSuccess(true);
      setPreview(null);
      setFrozen(null);
      const refreshed = await editor.refetch();
      if (!mounted.current || revision !== epoch.current) return;
      if (refreshed.error) setError(t('tagEditor.refreshFailed'));
      else {
        setBaseline(refreshed.data!);
        setSelected(
          new Map(
            refreshed
              .data!.nonAutomatic.filter((a) => a.active)
              .map((a) => [a.tagId, a.definition.name]),
          ),
        );
      }
      void qc.invalidateQueries({ queryKey: ['member-tags-summary', context] });
      void qc.invalidateQueries({ queryKey: ['member-tags-detail'] });
      void qc.invalidateQueries({ queryKey: ['members'] });
    } catch (e) {
      if (!mounted.current || revision !== epoch.current) return;
      const code = codeOf(e);
      // A later denial cannot prove an earlier request did not commit.
      if (wasUnknown || !code) {
        setUnknown(true);
        setError(t('tagEditor.unknown'));
      } else {
        setError(message(e));
        if (code === 'TAG_PLAN_STALE') {
          setSinglePreview(null);
          setSingleFrozen(null);
          setPreview(null);
          setFrozen(null);
          await refreshDraft();
        }
      }
    } finally {
      lock.current = false;
      if (mounted.current && revision === epoch.current) setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-3">
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tag-editor-title"
        tabIndex={-1}
        className="max-h-[90dvh] w-full max-w-2xl overflow-y-auto rounded-lg bg-base-100 p-4 shadow-xl"
      >
        <h2 id="tag-editor-title" className="mb-3 text-lg font-semibold">
          {t(writable ? 'tagEditor.manage' : 'tagEditor.view')}
        </h2>
        <label className="block">
          {t('tagEditor.search')}
          <input
            maxLength={100}
            className={`${control} w-full`}
            value={search}
            disabled={blocked || !!preview || !!single}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="my-3 block">
          {t('tags.scope')}
          <select
            className={`${control} ml-2 max-w-full`}
            value={scope}
            disabled={blocked}
            onChange={(e) => {
              const next = e.target.value as MemberTagScope;
              if (dirty) setDiscard(next);
              else reset(next);
            }}
          >
            {(
              baseline?.scopes ?? [
                { scope: 'VENUE', name: '' },
                { scope: 'ASSOCIATION', name: '' },
              ]
            ).map((s) => (
              <option key={s.scope} value={s.scope}>
                {s.scope === 'VENUE' ? `${t('tags.VENUE')} ${s.name}` : t('tags.ASSOCIATION')}
              </option>
            ))}
          </select>
        </label>
        {discard ? (
          <div role="alert" className="rounded border border-warning p-3">
            <p>{t('tagEditor.discard')}</p>
            <button className={control} onClick={() => setDiscard(null)}>
              {t('tagEditor.keep')}
            </button>
            <button
              className={control}
              onClick={() => (discard === 'CLOSE' ? onClose() : reset(discard))}
            >
              {t('tagEditor.discardConfirm')}
            </button>
          </div>
        ) : (
          <>
            {(editor.isPending || !baseline) && (
              <p>{editor.isError ? t('tags.loadFailed') : t('tags.loading')}</p>
            )}
            {baseline && (
              <>
                {!writable && <p>{t('tagEditor.readOnly')}</p>}
                {success && <p role="status">{t('tagEditor.saved')}</p>}
                {single ? (
                  <section>
                    <h3>{single.assignment.definition.name}</h3>
                    <p>
                      {single.assignment.expiresAt ?? t('tagEditor.unlimited')} →{' '}
                      {single.action === 'REMOVE'
                        ? t('tagEditor.removed')
                        : expiresOn || t('tagEditor.unlimited')}
                    </p>
                    <p>{t('tagEditor.sourceNotice')}</p>
                    {single.action === 'SET_EXPIRY' && (
                      <label>
                        {t('tagEditor.existingExpiry')}
                        <input
                          className={control}
                          type="date"
                          value={expiresOn}
                          disabled={blocked || !!singlePreview}
                          onChange={(e) => setExpiresOn(e.target.value)}
                        />
                      </label>
                    )}
                    {singlePreview?.errors.map((e, i) => (
                      <p role="alert" key={i}>
                        {t(`tags.errors.${e.code}`, { defaultValue: t('tags.errors.FAILED') })}
                      </p>
                    ))}
                    {singlePreview ? (
                      <button
                        className={control}
                        disabled={busy || !singlePreview.token}
                        onClick={() => void confirm()}
                      >
                        {t(unknown ? 'tagEditor.retryOriginal' : 'tags.confirm')}
                      </button>
                    ) : (
                      <button
                        className={control}
                        disabled={blocked}
                        onClick={() => void drySingle()}
                      >
                        {t('tags.preview')}
                      </button>
                    )}
                    <button
                      className={control}
                      disabled={blocked}
                      onClick={() => {
                        setSingle(null);
                        setSinglePreview(null);
                        setSingleFrozen(null);
                      }}
                    >
                      {t('tagEditor.back')}
                    </button>
                  </section>
                ) : preview ? (
                  <section aria-label={t('tags.previewTitle')}>
                    <h3>{t('tags.previewTitle')}</h3>
                    <p>{t('tagEditor.sourceNotice')}</p>
                    {preview.changes.map((c) => (
                      <p key={c.tagId} className="break-words">
                        {c.name} · {t(`tagEditor.${c.action}`)} · {t(`tags.${scope}`)} ·{' '}
                        {c.before?.expiresAt ?? t('tagEditor.unlimited')} →{' '}
                        {c.after?.expiresAt ??
                          t(c.action === 'REMOVE' ? 'tagEditor.removed' : 'tagEditor.unlimited')}
                      </p>
                    ))}
                    {preview.noops.map((n) => (
                      <p key={n.tagId}>
                        {selected?.get(n.tagId)} · {t('tagEditor.unchanged')}
                      </p>
                    ))}
                    {preview.errors.map((e, i) => (
                      <p role="alert" key={i}>
                        {e.tagId && selected?.get(e.tagId)}{' '}
                        {t(`tags.errors.${e.code}`, { defaultValue: t('tags.errors.FAILED') })}
                      </p>
                    ))}
                    <button
                      className={control}
                      disabled={blocked}
                      onClick={() => {
                        setPreview(null);
                        setFrozen(null);
                      }}
                    >
                      {t('tagEditor.back')}
                    </button>
                    <button
                      className={`${control} bg-primary text-primary-content`}
                      disabled={busy || !preview.token || (!unknown && !writable)}
                      onClick={() => void confirm()}
                    >
                      {t(unknown ? 'tagEditor.retryOriginal' : 'tags.confirm')}
                    </button>
                  </section>
                ) : (
                  <>
                    <div className="my-2 flex flex-wrap gap-2">
                      {[...(selected ?? [])].map(([id, name]) => (
                        <button
                          key={id}
                          className={`${control} max-w-full break-words`}
                          disabled={!writable || blocked || success}
                          onClick={() => toggle(id, name)}
                        >
                          {name}
                          {writable ? ' ×' : ''}
                        </button>
                      ))}
                    </div>
                    {writable && !success && (
                      <>
                        {catalogue.isError ? (
                          <p role="alert">{t('tags.loadFailed')}</p>
                        ) : (
                          catalogue.data?.data.map((tag) => (
                            <label
                              key={tag.id}
                              className="flex min-h-11 items-center gap-3 break-words"
                            >
                              <input
                                type="checkbox"
                                className="shrink-0"
                                checked={selected?.has(tag.id) ?? false}
                                disabled={blocked}
                                onChange={() => toggle(tag.id, tag.name)}
                              />
                              <span className="min-w-0 break-words">{tag.name}</span>
                            </label>
                          ))
                        )}
                        <div className="flex gap-2">
                          <button
                            className={control}
                            disabled={blocked || page === 1}
                            onClick={() => setPage(page - 1)}
                          >
                            {t('tags.previous')}
                          </button>
                          <span>{page}</span>
                          <button
                            className={control}
                            disabled={
                              blocked || !catalogue.data || page * 100 >= catalogue.data.total
                            }
                            onClick={() => setPage(page + 1)}
                          >
                            {t('tags.next')}
                          </button>
                        </div>
                        <label className="my-3 block">
                          {t('tagEditor.newExpiry')}
                          <input
                            className={`${control} ml-2`}
                            type="date"
                            value={expiresOn}
                            disabled={blocked}
                            onChange={(e) => setExpiresOn(e.target.value)}
                          />
                        </label>
                        <button
                          className={`${control} bg-primary text-primary-content`}
                          disabled={blocked || !dirty || add.length + remove.length > 100}
                          onClick={() => void dry()}
                        >
                          {t('tags.preview')}
                        </button>
                      </>
                    )}
                    <h3 className="mt-4 font-semibold">{t('tagEditor.sources')}</h3>
                    {baseline.nonAutomatic.map((a) => (
                      <div key={a.assignmentId} className="break-words">
                        {a.definition.name} · {t(`tags.${a.source}`)} · {t(`tags.${a.scope}`)} ·{' '}
                        {a.expiresAt ?? t('tagEditor.unlimited')}
                        {!a.active && ` · ${t('tagEditor.history')}`}
                        {writable && a.open && !dirty && !success && (
                          <>
                            <button
                              className={control}
                              disabled={blocked}
                              onClick={() => {
                                setSingle({ assignment: a, action: 'REMOVE' });
                                setExpiresOn('');
                              }}
                            >
                              {t('tagEditor.REMOVE')}
                            </button>
                            {a.active && !a.definition.archivedAt && (
                              <button
                                className={control}
                                disabled={blocked}
                                onClick={() => {
                                  setSingle({ assignment: a, action: 'SET_EXPIRY' });
                                  setExpiresOn(
                                    a.expiresAt
                                      ? new Date(Date.parse(a.expiresAt) - 1).toLocaleDateString(
                                          'en-CA',
                                          { timeZone: 'Asia/Taipei' },
                                        )
                                      : '',
                                  );
                                }}
                              >
                                {t('tagEditor.existingExpiry')}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                    {baseline.automatic.map((a, i) => (
                      <p key={`${a.tagId}:${i}`} className="break-words">
                        {a.name} · {t('tags.AUTOMATIC')} · {t(`tags.${a.scope}`)} · {a.computedAt}
                      </p>
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}
        {error && (
          <p role="alert" className="my-3 text-error">
            {error}
          </p>
        )}
        <button className={`${control} mt-4`} disabled={blocked} onClick={requestClose}>
          {t(success ? 'tags.done' : 'tagEditor.close')}
        </button>
      </div>
    </div>
  );
}
